/**
 * invoiceBaseOnly — 請求書の検算で止まったとき「基本料金だけで一旦請求書を発行しますか？」を出す（2026-11-03 前田さん指示）
 *
 * square_create_invoice.php は明細合計 ≠ 予約の合計のとき error_code=INVOICE_SUM_MISMATCH と mismatch（数字）を返す。
 * サイドパネル（useSquareInvoice）はそれを見て確認ダイアログを出し、「はい」なら base_only=true で作り直す。
 * 「基本料金だけ」の請求書は車両の行だけ（クーポン・ポイントは引けるときだけ引く。中身は PHP の invoiceBaseOnlyPlan）。
 * 作ったら予約の memo に印を残す（足りない分をあとで追加請求するため）。
 * m_trigger の自動作成・AI エージェントの作成ではダイアログを出さない（今までどおり止める）。
 * 正: md/master_md/m-trigger-req_gmail_invoice.md §14.3.3
 */

export interface InvoiceSumMismatch {
  built_sum: number;
  expected_sum: number;
  diff: number;
  base_price: number | null;
  base_only_amount: number | null;
  base_only_discounts: boolean;
  lines?: { name: string; amount: number }[];
  discount_total?: number;
}

const yen = (n: number) => `¥${Math.round(n).toLocaleString('ja-JP')}`;

/** 応答が検算の不一致で、基本料金だけで作れるときだけ mismatch を返す */
export function baseOnlyCandidate(data: any): InvoiceSumMismatch | null {
  if (!data || data.error_code !== 'INVOICE_SUM_MISMATCH' || !data.mismatch) return null;
  const m = data.mismatch as InvoiceSumMismatch;
  if (!m.base_only_amount || m.base_only_amount <= 0) return null;
  return m;
}

/** 確認ダイアログの文面（理由＝差額と、基本料金だけで作るときの金額を見せる） */
export function buildBaseOnlyConfirmMessage(m: InvoiceSumMismatch): string {
  const shortfall = m.expected_sum - (m.base_only_amount ?? 0);
  const lines = [
    '請求書の明細の合計が予約の合計と合わないため、請求書を作れませんでした。',
    '',
    `　明細の合計: ${yen(m.built_sum)}`,
    `　予約の合計: ${yen(m.expected_sum)}`,
    `　差: ${m.diff > 0 ? '+' : '−'}${yen(Math.abs(m.diff))}`,
  ];
  if (m.lines && m.lines.length > 0) {
    lines.push('', '組み立てた明細:');
    for (const l of m.lines) lines.push(`　${l.name} ${yen(l.amount)}`);
    if (m.discount_total) lines.push(`　割引 −${yen(m.discount_total)}`);
  }
  lines.push(
    '',
    '基本料金だけで一旦請求書を発行しますか？',
    `　請求額: ${yen(m.base_only_amount ?? 0)}` +
      (m.base_only_discounts && m.base_price && m.base_price !== m.base_only_amount
        ? `（基本料金 ${yen(m.base_price)} − クーポン・ポイント）`
        : '（基本料金）'),
    `　あとで追加請求が必要な額: ${yen(shortfall)}`,
    '',
    '［OK］基本料金だけで発行する　［キャンセル］何もしないで戻る',
  );
  return lines.join('\n');
}

/** 予約の memo に足す印（1行） */
export function buildBaseOnlyMemoLine(m: InvoiceSumMismatch, invoiceId: string, now: Date = new Date()): string {
  const d = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const amount = m.base_only_amount ?? 0;
  return (
    `【基本料金のみ請求 ${d}】請求 ${yen(amount)}／予約合計 ${yen(m.expected_sum)}／` +
    `追加請求が必要 ${yen(m.expected_sum - amount)}（${invoiceId}）`
  );
}

/** memo の末尾に印を足す（既存の memo は残す） */
export function appendBaseOnlyMemo(memo: string | null | undefined, line: string): string {
  const cur = String(memo ?? '').replace(/\s+$/, '');
  return cur ? `${cur}\n${line}` : line;
}
